import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
  Observable,
  catchError,
  debounceTime,
  distinctUntilChanged,
  forkJoin,
  map,
  of,
  skip,
  switchMap
} from 'rxjs';

import { DataSpaceModel } from '@core/models/dataspace/data-space.model';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { DataspaceService } from '@core/services/dataspace/dataspace.service';
import { ICONS } from '@core/services/icons/icons.service';

import { ButtonSelectComponent } from '@shared/components/button-select/button-select.component';
import { SelectValue } from '@shared/components/select/select-item.model';
import { SelectItem } from '@shared/components/select/select-item.model';

const SEARCH_DEBOUNCE_MS = 300;

@Component({
  selector: 'app-dataspace-selector',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonSelectComponent],
  template: `
    <app-button-select
      [items]="dataspaceItems()"
      [value]="multiple() ? values() : value()"
      [multiple]="multiple()"
      (valueChange)="onValueChange($event)"
      (searchChange)="onSearchChange($event)"
      (loadMore)="onLoadMore()"
      [placeholder]="resolvedPlaceholder()"
      [searchPlaceholder]="searchPlaceholder()"
      [emptyMessage]="emptyMessage()"
      [triggerIconClass]="triggerIconClass()"
      [disabled]="disabled()" />
  `,
  styles: ``
})
export class DataspaceSelectorComponent {
  value = input<number | null>(null);
  // Multi-select mode: when `multiple` is true the component selects a set of
  // dataspaces via `values`/`valuesChange` instead of the single `value`.
  multiple = input<boolean>(false);
  values = input<number[]>([]);
  placeholder = input<string | null>(null);
  triggerIconClass = input<string | null>(ICONS.database);
  disabled = input<boolean>(false);

  valueChange = output<number | null>();
  valuesChange = output<number[]>();
  touchedChange = output<void>();

  private readonly dataspaceService = inject(DataspaceService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly currentLang = inject(CurrentLangService).currentLang;

  protected readonly search = signal<string>('');
  protected readonly dataspaces = signal<DataSpaceModel[]>([]);
  protected readonly selectedDataspace = signal<DataSpaceModel | null>(null);
  protected readonly selectedDataspaces = signal<DataSpaceModel[]>([]);
  protected readonly nextCursor = signal<number | null>(null);
  protected readonly loading = signal<boolean>(false);

  // Ensures the selected dataspace(s) are always present in the dropdown items,
  // so labels resolve even when an id isn't in the currently loaded page.
  protected readonly dataspaceItems = computed<SelectItem<number>[]>(() => {
    const items = this.dataspaces().map((ds) => ({ id: ds.id, label: ds.name }));
    if (this.multiple()) {
      const missing = this.selectedDataspaces()
        .filter((sel) => !items.some((it) => it.id === sel.id))
        .map((sel) => ({ id: sel.id, label: sel.name }));
      return [...missing, ...items];
    }
    const selected = this.selectedDataspace();
    if (selected && !items.some((it) => it.id === selected.id)) {
      return [{ id: selected.id, label: selected.name }, ...items];
    }
    return items;
  });

  protected readonly resolvedPlaceholder = computed<string>(() => {
    this.currentLang();
    return this.placeholder() ?? this.translate.instant('topbar.dataspace.select');
  });

  protected readonly searchPlaceholder = computed<string>(() => {
    this.currentLang();
    return this.translate.instant('common.search_placeholder');
  });

  protected readonly emptyMessage = computed<string>(() => {
    this.currentLang();
    return this.translate.instant('admin.user_form.no_results');
  });

  constructor() {
    this.subscribeToFirstPage(this.dataspaceService.getDataSpaces$(''));
    this.subscribeToFirstPage(
      toObservable(this.search).pipe(
        skip(1),
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap((term) => {
          this.loading.set(true);
          return this.dataspaceService.getDataSpaces$(term);
        })
      )
    );

    toObservable(this.value)
      .pipe(
        distinctUntilChanged(),
        switchMap((id) => {
          if (id === null) return of(null);
          const known = this.dataspaces().find((ds) => ds.id === id);
          if (known) return of(known);
          return this.dataspaceService
            .getDataSpaceById$(id)
            .pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((ds) => this.selectedDataspace.set(ds));

    // Multi-select: resolve the selected ids to dataspaces so their labels are
    // always available, fetching any that aren't in the loaded pages.
    toObservable(this.values)
      .pipe(
        distinctUntilChanged(
          (a, b) => a.length === b.length && a.every((v, i) => v === b[i])
        ),
        switchMap((ids) => {
          if (!ids || ids.length === 0) return of([] as DataSpaceModel[]);
          const known = this.dataspaces();
          const missing = ids.filter((id) => !known.some((ds) => ds.id === id));
          if (missing.length === 0) {
            return of(
              ids
                .map((id) => known.find((ds) => ds.id === id))
                .filter((ds): ds is DataSpaceModel => ds !== undefined)
            );
          }
          return forkJoin(
            missing.map((id) =>
              this.dataspaceService.getDataSpaceById$(id).pipe(catchError(() => of(null)))
            )
          ).pipe(
            map((fetched) => {
              const all: DataSpaceModel[] = [...known];
              for (const ds of fetched) {
                if (ds) all.push(ds);
              }
              const ordered: DataSpaceModel[] = [];
              for (const id of ids) {
                const found = all.find((ds) => ds.id === id);
                if (found) ordered.push(found);
              }
              return ordered;
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((list) => this.selectedDataspaces.set(list));
  }

  private subscribeToFirstPage(
    source$: Observable<{ dataSpaces: DataSpaceModel[]; cursor: number }>
  ): void {
    this.loading.set(true);
    source$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (page) => this.applyPage(page, true),
      error: () => this.loading.set(false)
    });
  }

  protected onValueChange(value: SelectValue): void {
    if (this.multiple()) {
      const ids = Array.isArray(value)
        ? (value as number[])
        : value == null
          ? []
          : [value as number];
      this.valuesChange.emit(ids);
      return;
    }
    const id = Array.isArray(value) ? null : (value as number | null);
    this.valueChange.emit(id);
  }

  protected onSearchChange(term: string): void {
    this.search.set(term);
  }

  protected onLoadMore(): void {
    if (this.loading()) return;
    const cursor = this.nextCursor();
    if (cursor === null) return;
    this.loading.set(true);
    this.dataspaceService
      .getDataSpaces$(this.search(), cursor)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => this.applyPage(page, false),
        error: () => this.loading.set(false)
      });
  }

  private applyPage(
    page: { dataSpaces: DataSpaceModel[]; cursor: number },
    replace: boolean
  ): void {
    this.dataspaces.update((current) =>
      replace ? page.dataSpaces : [...current, ...page.dataSpaces]
    );
    this.nextCursor.set(page.cursor ?? null);
    this.loading.set(false);
  }
}
